import { useRef } from "react";
import { X, Upload, BookmarkPlus } from "lucide-react";
import { PRESET_STYLES } from "@/lib/decartApi";
import { SavedPresetTile } from "./SavedPresetTile";
import { SaveStyleForm } from "./SaveStyleForm";

const tileCls = (active) =>
    `group relative aspect-square border bg-obsidian overflow-hidden transition-all duration-200 ${
        active ? "border-blaze ring-1 ring-blaze" : "border-white/10 hover:border-white/40"
    }`;

const TileSubline = ({ active, activeText, idleText }) => {
    if (active && activeText) {
        return <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-blaze mt-1">● {activeText}</div>;
    }
    if (idleText) {
        return <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/30 mt-1">{idleText}</div>;
    }
    return null;
};

const TileLabel = ({ title, activeText, idleText, active }) => (
    <div className="absolute bottom-2 left-2 right-2 text-left">
        <div className="font-display font-bold uppercase text-sm leading-tight text-white">{title}</div>
        <TileSubline active={active} activeText={activeText} idleText={idleText} />
    </div>
);

const NoneTile = ({ active, onClick }) => (
    <button onClick={onClick} data-testid="preset-chip-none" className={tileCls(active)}>
        <div className="absolute inset-0 flex items-center justify-center">
            <X
                className={`w-10 h-10 transition-colors ${
                    active ? "text-blaze" : "text-white/30 group-hover:text-white/70"
                }`}
                strokeWidth={1.5}
            />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
        <TileLabel title="None" activeText="Passthrough" idleText="Original" active={active} />
    </button>
);

const PresetTile = ({ preset, active, onClick }) => (
    <button onClick={onClick} data-testid={`preset-chip-${preset.id}`} className={tileCls(active)}>
        <img
            src={preset.thumb}
            alt={preset.label}
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 ${
                active ? "grayscale-0 scale-105" : "grayscale group-hover:grayscale-0"
            }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <TileLabel title={preset.label} activeText="Active" active={active} />
    </button>
);

const CustomTile = ({ active, referencePreview, onUpload }) => {
    const inputRef = useRef(null);
    return (
        <button onClick={() => inputRef.current?.click()} data-testid="preset-chip-custom" className={tileCls(active)}>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={onUpload}
                className="hidden"
                data-testid="custom-chip-input"
            />
            {active && referencePreview ? (
                <img
                    src={referencePreview}
                    alt="Custom style"
                    className="absolute inset-0 w-full h-full object-cover scale-105"
                />
            ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                    <Upload className="w-10 h-10 text-white/30 group-hover:text-white/70 transition-colors" strokeWidth={1.5} />
                </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <TileLabel title="Custom" activeText="Your style" idleText="Upload pic" active={active} />
        </button>
    );
};

const SaveTile = ({ onClick }) => (
    <button
        onClick={onClick}
        data-testid="preset-chip-save"
        className="group relative aspect-square border border-dashed border-white/15 hover:border-blaze/60 bg-obsidian overflow-hidden transition-all duration-200"
    >
        <div className="absolute inset-0 flex items-center justify-center">
            <BookmarkPlus className="w-10 h-10 text-white/30 group-hover:text-blaze transition-colors" strokeWidth={1.5} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
        <TileLabel title="Save" idleText="Name style" active={false} />
    </button>
);

export const QuickStyles = ({
    activePreset,
    referencePreview,
    savedPresets,
    showSaveInput,
    presetName,
    onNone,
    onPreset,
    onCustomUpload,
    onSavedPreset,
    onDeleteSaved,
    onToggleSaveForm,
    onPresetNameChange,
    onSaveConfirm,
    onSaveCancel,
}) => (
    <div className="border border-white/10 bg-surface p-5">
        <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">Quick Styles</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">Tap to apply instantly</div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            <NoneTile active={activePreset === "none"} onClick={onNone} />
            {PRESET_STYLES.map((p) => (
                <PresetTile key={p.id} preset={p} active={activePreset === p.id} onClick={() => onPreset(p)} />
            ))}
            <CustomTile active={activePreset === "custom"} referencePreview={referencePreview} onUpload={onCustomUpload} />
            {savedPresets.map((p) => (
                <SavedPresetTile
                    key={p.id}
                    preset={p}
                    active={activePreset === `saved-${p.id}`}
                    onApply={() => onSavedPreset(p)}
                    onDelete={() => onDeleteSaved(p.id)}
                />
            ))}
            <SaveTile onClick={onToggleSaveForm} />
        </div>
        {showSaveInput && (
            <SaveStyleForm
                name={presetName}
                onNameChange={onPresetNameChange}
                onConfirm={onSaveConfirm}
                onCancel={onSaveCancel}
            />
        )}
    </div>
);
